import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle2, Circle, TrendingUp, Clock, Sparkles,
  Target, ArrowRight, RefreshCw, Loader2, ChevronRight,
  Award, Zap
} from 'lucide-react';
import useSafeBackNavigation from '../hooks/useSafeBackNavigation';
import useSafeFetch from '../hooks/useSafeFetch';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = getApiUrl();
const MyOwenPath = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = useSafeBackNavigation('/training');
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  
  // 🔒 P0 FIX: useSafeFetch avec AbortController + native fetch (anti rrweb clone)
  const { safeFetch, isAborted } = useSafeFetch();
  const isMountedRef = useRef(true);

  // Cleanup au démontage
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Always fetch fresh pathway on mount - no caching, key by user.id
  useEffect(() => {
    if (user?.email) {
      fetchPathway();
    }
  }, [user?.email]);

  const fetchPathway = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Add cache-busting timestamp
      const timestamp = new Date().getTime();
      // Use V2 pathway endpoint with user email
      const url = `${BACKEND_URL}/api/owen/pathway/${encodeURIComponent(user?.email)}?t=${timestamp}`;
      
      // 🔒 P0 FIX: Use safeFetch (native fetch + AbortController + single body read)
      const data = await safeFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        timeout: 30000
      });

      // 🔒 Guard: Ne pas mettre à jour si composant démonté
      if (!isMountedRef.current) return;

      setPathway(data);
      setRenderKey(prev => prev + 1);
      
    } catch (err) {
      // 🔒 P0 FIX: Ignorer les erreurs d'abort (navigation)
      if (isAborted(err)) {
        console.log('[MyOwenPath] Request aborted (navigation)');
        return;
      }
      
      // 🔒 Guard: Ne pas mettre à jour si composant démonté
      if (!isMountedRef.current) return;
      
      // Handle HTTP status errors
      if (err.status === 404) {
        setError('no_pathway');
      } else if (err.status === 403) {
        setError('premium_required');
      } else {
        setError(err.message || 'Erreur de connexion');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleRegeneratePath = async () => {
    if (!window.confirm('🔄 Êtes-vous sûr de vouloir régénérer votre parcours ? Cela créera un nouveau parcours basé sur vos dernières réponses.')) {
      return;
    }

    try {
      setRegenerating(true);
      const token = localStorage.getItem('token');
      
      // 🔒 P0 FIX: Use safeFetch (native fetch + AbortController + single body read)
      const result = await safeFetch(`${BACKEND_URL}/api/owen/pathway/clean-regenerate/${user.email}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 60000 // Régénération peut être longue
      });
      
      // 🔒 Guard: Ne pas continuer si composant démonté
      if (!isMountedRef.current) return;
      
      // Show success message
      alert('🧠 Nouveau parcours généré avec succès selon vos dernières réponses !');
      
      // Reload pathway immediately with cache-bust
      await fetchPathway();
      
    } catch (err) {
      // 🔒 P0 FIX: Ignorer les erreurs d'abort
      if (isAborted(err)) return;
      
      if (isMountedRef.current) {
        alert(`Erreur: ${err.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setRegenerating(false);
      }
    }
  };

  const getModuleStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-6 h-6 text-green-400" />;
      case 'in_progress':
        return <Clock className="w-6 h-6 text-amber-400" />;
      default:
        return <Circle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getModuleStatusBadge = (status, source) => {
    if (status === 'completed') {
      return (
        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Terminé
        </span>
      );
    }
    if (status === 'in_progress') {
      return (
        <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium flex items-center gap-1">
          <Clock className="w-3 h-3" />
          En cours
        </span>
      );
    }
    if (source === 'dynamic') {
      return (
        <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          🎯 Personnalisé
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg">Chargement de votre parcours...</p>
        </div>
      </div>
    );
  }

  if (error === 'no_pathway') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        {/* 🔒 P0 FIX: Bouton retour Dashboard */}
        <button
          onClick={() => navigate('/training')}
          className="absolute top-6 left-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
          <span>Retour Dashboard</span>
        </button>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 text-center"
        >
          <Target className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Aucun parcours trouvé</h2>
          <p className="text-gray-300 mb-6">
            Créez votre parcours personnalisé en quelques minutes avec Owen
          </p>
          <button
            onClick={() => navigate('/training?openIntake=true')}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
          >
            Commencer maintenant
          </button>
        </motion.div>
      </div>
    );
  }

  if (error === 'premium_required') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 text-center"
        >
          {/* Crown Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
            <Award className="w-10 h-10 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-3">
            Abonnement Premium Requis
          </h2>

          {/* Description */}
          <p className="text-gray-300 mb-8 text-lg leading-relaxed">
            Le <span className="font-semibold text-purple-400">Parcours Owen 360°</span> est une fonctionnalité premium exclusive. 
            Passez au Premium pour débloquer votre parcours personnalisé créé par IA.
          </p>

          {/* Benefits List */}
          <div className="bg-white/5 rounded-2xl p-6 mb-8 text-left">
            <h3 className="text-white font-semibold mb-4 text-center">Avec Premium, vous accédez à :</h3>
            <ul className="space-y-3">
              {[
                'Parcours Owen 360° personnalisé par IA',
                '20 modules premium complets',
                'Coach vocal Owen illimité',
                'Scénarios IA immersifs',
                'Suivi de progression détaillé'
              ].map((benefit, index) => (
                <li key={index} className="flex items-center gap-3 text-gray-300">
                  <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/subscription', { state: { from: location.pathname } })}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-amber-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all transform hover:scale-105"
            >
              Passer au Premium
            </button>
            <button
              onClick={() => navigate('/training')}
              className="w-full px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all"
            >
              Retour au dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">Erreur: {error}</p>
          <button
            onClick={fetchPathway}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              Retour au dashboard
            </button>
          </div>

          <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400 bg-clip-text text-transparent mb-3">
            Mon Parcours Owen
          </h1>
          <p className="text-gray-300 text-lg">
            Votre parcours personnalisé de {pathway?.modules?.length || 0} modules
          </p>
        </motion.div>

        {/* Progress Overview Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-purple-400" />
                Progression Globale
              </h2>
              <p className="text-gray-400">
                {pathway?.modules?.filter(m => m.status === 'completed').length || 0} / {pathway?.modules?.length || 0} modules complétés
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {pathway?.modules?.length > 0 
                  ? Math.round((pathway.modules.filter(m => m.status === 'completed').length / pathway.modules.length) * 100) 
                  : 0}%
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative w-full h-3 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ 
                width: pathway?.modules?.length > 0 
                  ? `${(pathway.modules.filter(m => m.status === 'completed').length / pathway.modules.length) * 100}%`
                  : '0%'
              }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 rounded-full"
            />
          </div>

          {/* ✅ Indicateurs secondaires supprimés (Modules terminés, En cours, Modules IA personnalisés)
              La progression globale et le total X/Y modules sont conservés ci-dessus */}
        </motion.div>

        {/* Modules Timeline */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-400" />
            Votre Timeline
          </h2>

          <AnimatePresence>
            {pathway?.modules?.map((module, index) => (
              <motion.div
                key={`${module.module_id || module.id}-v${pathway.version || 1}-${renderKey}-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative"
              >
                <div 
                  onClick={() => {
                    // Navigate based on source
                    if (module.source === 'dynamic') {
                      // Extract UUID from "dynamic:<uuid>" format
                      const dynamicId = module.module_id.replace('dynamic:', '');
                      navigate(`/modules-dynamic/${dynamicId}`);
                    } else {
                      // Catalog module - use numeric ID
                      navigate(`/module-v2/${module.module_id}`);
                    }
                  }}
                  className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 hover:bg-white/10 transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getModuleStatusIcon(module.status || 'not_started')}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-400">
                              Module {module.order || index + 1}
                            </span>
                            {getModuleStatusBadge(module.status || 'not_started', module.source)}
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                            {module.module_title}
                          </h3>
                          {module.objectives && module.objectives.length > 0 && (
                            <p className="text-sm text-gray-400 mb-2">
                              {module.objectives[0]}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {module.estimated_duration || '30 min'}
                            </span>
                            <span className="flex items-center gap-1">
                              Pertinence: {Math.round((module.relevance || 0.8) * 100)}%
                            </span>
                            {module.source === 'dynamic' && (
                              <span className="flex items-center gap-1 text-purple-400">
                                <Zap className="w-4 h-4" />
                                Généré par IA
                              </span>
                            )}
                          </div>
                        </div>

                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline connector */}
                {index < (pathway?.modules?.length || 0) - 1 && (
                  <div className="absolute left-[31px] top-full w-0.5 h-4 bg-gradient-to-b from-white/20 to-transparent" />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-xl rounded-3xl border border-purple-500/30 p-8 text-center"
        >
          <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          
          {(() => {
            const completedCount = pathway?.modules?.filter(m => m.status === 'completed').length || 0;
            const totalCount = pathway?.modules?.length || 0;
            const allCompleted = completedCount === totalCount && totalCount > 0;
            const nextModule = pathway?.modules?.find(m => m.status === 'not_started' || m.status === 'in_progress');
            
            if (allCompleted) {
              return (
                <>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    🎉 Félicitations ! Parcours terminé !
                  </h3>
                  <p className="text-gray-300 mb-6">
                    Vous avez complété tous vos modules. Générez un nouveau parcours pour continuer à progresser.
                  </p>
                  <button
                    onClick={handleRegeneratePath}
                    disabled={regenerating}
                    className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-5 h-5 ${regenerating ? 'animate-spin' : ''}`} />
                    Générer un nouveau parcours
                  </button>
                </>
              );
            }
            
            return (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Prêt à continuer votre développement ?
                </h3>
                <p className="text-gray-300 mb-6">
                  {nextModule ? 
                    `Owen vous attend pour le module "${nextModule.module_title}"` :
                    'Owen vous accompagne à chaque étape'
                  }
                </p>
                <button
                  onClick={() => {
                    if (nextModule) {
                      // Navigate based on source
                      if (nextModule.source === 'dynamic') {
                        const dynamicId = nextModule.module_id.replace('dynamic:', '');
                        navigate(`/modules-dynamic/${dynamicId}`);
                      } else {
                        navigate(`/module-v2/${nextModule.module_id}`);
                      }
                    } else {
                      navigate('/training');
                    }
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all inline-flex items-center gap-2"
                >
                  Continuer mon parcours
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            );
          })()}
        </motion.div>
      </div>
    </div>
  );
};

export default MyOwenPath;
